import { useCallback, useEffect, useMemo } from 'react';
import { AssetProcessingStateId } from '@packages/models/api';
import type { DraggableWorksheetFormComponent, FormElementFile } from '@packages/models/api';

// Define a generic element type that can contain file information
type ElementWithFileUpload =
    | {
          formElementFile?: FormElementFile;
          options?: Array<{
              optionFile?: FormElementFile;
          }>;
          optionConditionsElements?: ElementWithFileUpload[];
      }
    | {
          // Support for WorksheetFormElementEntity from domain layer
          formElementFile?: {
              status?: string;
          };
          options?: Array<{
              optionFile?: {
                  status?: string;
              };
          }>;
      };

/**
 * Hook to detect if any worksheet attachment uploads are currently in progress
 */
export function useWorksheetUploadGuard(elements?: (DraggableWorksheetFormComponent | ElementWithFileUpload)[]) {
    /**
     * Check if a single file is currently uploading
     */
    const isFileUploading = useCallback((file?: FormElementFile | { status?: string }): boolean => {
        return file?.status === AssetProcessingStateId.UPLOADING;
    }, []);

    /**
     * Check all worksheet elements to see if any have uploads in progress
     */
    const hasUploadsInProgress = useMemo(() => {
        if (!elements || !Array.isArray(elements)) {
            return false;
        }

        const uploadsDetected = elements.some((element) => {
            // Check main form element file
            if (isFileUploading(element.formElementFile)) {
                return true;
            }

            // Check option files (for conditional elements)
            if (element.options && Array.isArray(element.options)) {
                if (element.options.some((option) => isFileUploading(option.optionFile))) {
                    return true;
                }
            }

            // Check conditional elements (only for DraggableWorksheetFormComponent type)
            if (
                'optionConditionsElements' in element &&
                element.optionConditionsElements &&
                Array.isArray(element.optionConditionsElements)
            ) {
                if (
                    element.optionConditionsElements.some((conditionalElement) => {
                        // Check conditional element file
                        if (isFileUploading(conditionalElement.formElementFile)) {
                            return true;
                        }
                        // Check conditional element options
                        if (conditionalElement.options && Array.isArray(conditionalElement.options)) {
                            return conditionalElement.options.some((option) => isFileUploading(option.optionFile));
                        }
                        return false;
                    })
                ) {
                    return true;
                }
            }

            return false;
        });

        return uploadsDetected;
    }, [elements, isFileUploading]);

    return {
        hasUploadsInProgress,
        isFileUploading,
        warningMessage:
            'A file upload is currently in progress. Leaving this page may cancel the upload and cause your worksheet changes to be lost.\nAre you sure you want to leave?',
    };
}
